import { Copy, Import, Play, Square, type LucideIcon } from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
} from "react";
import { createPortal } from "react-dom";

import { COMPUTING_OPACITY_PULSE_CLASS } from "../statusStyles";
import type { CellCompareDiff, CellPreviewProps } from "../types";

import CellActions from "./CellActions";
import ErrorCellPreview from "./ErrorCellPreview";
import HumanInputCellPreview from "./HumanInputCellPreview";
import LinkCellPreview from "./LinkCellPreview";
import LLMAssertionCellPreview from "./LLMAssertionCellPreview";
import MediaCellPreview from "./MediaCellPreview";
import PendingCellPreview from "./PendingCellPreview";
import PromptRequestMetricsChip from "./PromptRequestMetricsChip";
import TextCellPreview from "./TextCellPreview";
import { STALE_TOOLTIP } from "./utils";

import { CellContentType } from "@/routes/smart-table/utils/contentType";

interface CellContextMenuAction {
  id: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
}

interface ContextMenuPosition {
  x: number;
  y: number;
}

const CONTEXT_MENU_VIEWPORT_PADDING = 8;
const ACTION_AFFORDANCE_HOVER_DELAY_MS = 90;

function CompactCellDiffPreview({ diff }: { diff: CellCompareDiff }) {
  const isEqualComparison = diff.beforeValue === diff.afterValue;

  if (isEqualComparison) {
    return (
      <div className="flex h-full w-full items-center px-2 text-xs text-muted-foreground">
        Values are equal
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden px-2 py-1 text-xs leading-5">
      <div className="min-w-0 truncate text-rose-700 dark:text-rose-300">
        <span className="mr-1 font-medium">−</span>
        {diff.beforeValue || " "}
      </div>
      <div className="min-w-0 truncate text-emerald-700 dark:text-emerald-300">
        <span className="mr-1 font-medium">+</span>
        {diff.afterValue || " "}
      </div>
    </div>
  );
}

function CellPreview({
  assertionModel,
  classified,
  displayText,
  hasContent,
  isComputedDisplay,
  isError,
  isJson,
  isLLMAssertion,
  isMedia,
  isPending,
  isHydrating,
  isVisuallyStale,
  isCompare,
  isHumanInput = false,
  humanInputConfig = null,
  humanInputValue = null,
  canEditHumanInput = false,
  onSaveHumanInput,
  compareDiff,
  mediaPayload,
  multiContentPayload,
  linkPayload,
  onOpenDialog,
  openRequestUrl,
  openRequestLabel,
  executionMetricsOverlay,
  canUpload,
  onUploadFiles,
  onCopyCell,
  onImportAnchor,
  importAnchorLabel,
  onRunCell,
  onStopCell,
  richHtml,
  smartComputedTextClass,
  computedBooleanValue,
  textClass,
  tooltipText,
  globalSearchQuery,
  userFacingError,
  leadingIcon = null,
}: CellPreviewProps) {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const hoverActivationTimeoutRef = useRef<number | null>(null);
  const [isActionAffordanceVisible, setIsActionAffordanceVisible] = useState(false);
  const [hasActivatedAffordances, setHasActivatedAffordances] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null);
  const [adjustedContextMenuPosition, setAdjustedContextMenuPosition] =
    useState<ContextMenuPosition | null>(null);
  const [activeContextMenuActions, setActiveContextMenuActions] = useState<CellContextMenuAction[]>(
    [],
  );

  const clearHoverActivationTimeout = useCallback(() => {
    if (hoverActivationTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(hoverActivationTimeoutRef.current);
    hoverActivationTimeoutRef.current = null;
  }, []);

  const showActionAffordances = useCallback(() => {
    clearHoverActivationTimeout();
    setHasActivatedAffordances(true);
    setIsActionAffordanceVisible(true);
  }, [clearHoverActivationTimeout]);

  const scheduleActionAffordances = useCallback(() => {
    if (isActionAffordanceVisible || hoverActivationTimeoutRef.current !== null) {
      return;
    }

    hoverActivationTimeoutRef.current = window.setTimeout(() => {
      hoverActivationTimeoutRef.current = null;
      setHasActivatedAffordances(true);
      setIsActionAffordanceVisible(true);
    }, ACTION_AFFORDANCE_HOVER_DELAY_MS);
  }, [isActionAffordanceVisible]);

  const hideActionAffordances = useCallback(() => {
    clearHoverActivationTimeout();
    setIsActionAffordanceVisible(false);
  }, [clearHoverActivationTimeout]);

  const handleActionAffordanceBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsActionAffordanceVisible(false);
  }, []);

  const shouldShowPromptMetrics = Boolean(
    executionMetricsOverlay?.cost || executionMetricsOverlay?.latency,
  );
  const shouldRenderMetricsOverlay = shouldShowPromptMetrics && !isPending && !isHydrating;

  const shouldShowUploadButton =
    canUpload &&
    (classified.type === CellContentType.EMPTY ||
      isMedia ||
      classified.type === CellContentType.MULTI_CONTENT);

  const uploadInput =
    hasActivatedAffordances && shouldShowUploadButton ? (
      <input
        ref={uploadInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={event => {
          const { files } = event.target;
          if (!files || files.length === 0) {
            return;
          }

          onUploadFiles?.(Array.from(files));
          event.currentTarget.value = "";
        }}
      />
    ) : null;

  const canExpandText = classified.type !== "empty" && (classified.isStructuredType || hasContent);

  let resolvedOnOpen = onOpenDialog;
  if (userFacingError?.isNetworkError || (isPending && !hasContent)) {
    resolvedOnOpen = undefined;
  } else if (!isLLMAssertion && !isCompare && !isMedia && !canExpandText) {
    resolvedOnOpen = undefined;
  }

  const shouldRenderPendingPreview = isPending && !isHydrating;
  const shouldRenderEmptyStalePreview = isVisuallyStale && !hasContent && !isHydrating;

  const buildContextMenuActions = useCallback((): CellContextMenuAction[] => {
    const actions: CellContextMenuAction[] = [];

    if (onImportAnchor) {
      actions.push({
        id: "import",
        label: importAnchorLabel ?? "Import into cell",
        icon: Import,
        onSelect: onImportAnchor,
      });
    }

    if (onCopyCell) {
      actions.push({
        id: "copy",
        label: "Copy",
        icon: Copy,
        onSelect: onCopyCell,
      });
    }

    if (onStopCell) {
      actions.push({
        id: "stop",
        label: "Stop",
        icon: Square,
        onSelect: onStopCell,
      });
    } else if (onRunCell) {
      actions.push({
        id: "run",
        label: "Run",
        icon: Play,
        onSelect: onRunCell,
      });
    }

    return actions;
  }, [importAnchorLabel, onCopyCell, onImportAnchor, onRunCell, onStopCell]);

  const closeContextMenu = useCallback(() => {
    setContextMenuPosition(null);
    setAdjustedContextMenuPosition(null);
    setActiveContextMenuActions([]);
  }, []);

  useEffect(
    () => () => {
      clearHoverActivationTimeout();
    },
    [clearHoverActivationTimeout],
  );

  useEffect(() => {
    if (!contextMenuPosition) {
      return;
    }

    document.addEventListener("mousedown", closeContextMenu);
    document.addEventListener("scroll", closeContextMenu, true);
    window.addEventListener("blur", closeContextMenu);
    window.addEventListener("resize", closeContextMenu);

    return () => {
      document.removeEventListener("mousedown", closeContextMenu);
      document.removeEventListener("scroll", closeContextMenu, true);
      window.removeEventListener("blur", closeContextMenu);
      window.removeEventListener("resize", closeContextMenu);
    };
  }, [closeContextMenu, contextMenuPosition]);

  useLayoutEffect(() => {
    if (!contextMenuPosition || activeContextMenuActions.length === 0) {
      setAdjustedContextMenuPosition(null);
      return;
    }

    const menuElement = contextMenuRef.current;
    if (!menuElement) {
      setAdjustedContextMenuPosition(contextMenuPosition);
      return;
    }

    const menuRect = menuElement.getBoundingClientRect();
    const maxLeft = window.innerWidth - menuRect.width - CONTEXT_MENU_VIEWPORT_PADDING;
    const maxTop = window.innerHeight - menuRect.height - CONTEXT_MENU_VIEWPORT_PADDING;

    setAdjustedContextMenuPosition({
      x: Math.max(CONTEXT_MENU_VIEWPORT_PADDING, Math.min(contextMenuPosition.x, maxLeft)),
      y: Math.max(CONTEXT_MENU_VIEWPORT_PADDING, Math.min(contextMenuPosition.y, maxTop)),
    });
  }, [activeContextMenuActions.length, contextMenuPosition]);

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      showActionAffordances();

      const nextContextMenuActions = buildContextMenuActions();
      if (nextContextMenuActions.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setActiveContextMenuActions(nextContextMenuActions);
      setAdjustedContextMenuPosition(null);
      setContextMenuPosition({ x: event.clientX, y: event.clientY });
    },
    [buildContextMenuActions, showActionAffordances],
  );

  let innerEl: React.ReactNode = null;

  if (isHumanInput && humanInputConfig && onSaveHumanInput) {
    innerEl = (
      <HumanInputCellPreview
        config={humanInputConfig}
        value={humanInputValue}
        isDisabled={!canEditHumanInput}
        onSave={onSaveHumanInput}
      />
    );
  } else if (shouldRenderPendingPreview || shouldRenderEmptyStalePreview) {
    if (shouldRenderPendingPreview && hasContent && isMedia && mediaPayload) {
      innerEl = (
        <div className={`pointer-events-none h-full w-full ${COMPUTING_OPACITY_PULSE_CLASS}`}>
          <MediaCellPreview media={mediaPayload} multiContent={multiContentPayload} />
        </div>
      );
    } else {
      const pendingPreview = (
        <PendingCellPreview
          displayText={displayText}
          hasContent={hasContent}
          tone={shouldRenderEmptyStalePreview ? "stale" : "pending"}
        />
      );
      innerEl = isLLMAssertion ? (
        <div className="flex h-full w-full items-center px-2">{pendingPreview}</div>
      ) : (
        pendingPreview
      );
    }
  } else if (userFacingError && isError) {
    innerEl = <ErrorCellPreview userFacingError={userFacingError} />;
  } else if (isLLMAssertion && assertionModel && !isError) {
    innerEl = <LLMAssertionCellPreview model={assertionModel} />;
  } else if (isCompare && compareDiff && !isError) {
    innerEl = <CompactCellDiffPreview diff={compareDiff} />;
  } else if (isMedia && !isError && mediaPayload) {
    innerEl = (
      <MediaCellPreview
        media={mediaPayload}
        multiContent={multiContentPayload}
        openDialog={onOpenDialog}
      />
    );
  } else if (classified.type === CellContentType.LINK && linkPayload && !isError) {
    innerEl = <LinkCellPreview link={linkPayload} openDialog={onOpenDialog} />;
  } else {
    innerEl = (
      <TextCellPreview
        isComputedDisplay={isComputedDisplay}
        isError={isError}
        isJson={isJson}
        richHtml={richHtml}
        smartComputedTextClass={smartComputedTextClass}
        computedBooleanValue={computedBooleanValue}
        textClass={textClass}
        tooltipText={tooltipText}
        globalSearchQuery={globalSearchQuery}
        leadingIcon={leadingIcon}
      />
    );
  }

  const shouldMountActions =
    Boolean(resolvedOnOpen) ||
    Boolean(onCopyCell) ||
    Boolean(onImportAnchor) ||
    Boolean(onRunCell) ||
    Boolean(onStopCell) ||
    Boolean(openRequestUrl) ||
    shouldShowUploadButton;
  const shouldShowActions =
    Boolean(onStopCell) || isActionAffordanceVisible || contextMenuPosition !== null;
  const shouldRenderActions = shouldMountActions && shouldShowActions;

  const contextMenu =
    contextMenuPosition && activeContextMenuActions.length > 0 && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={contextMenuRef}
            className="ag-custom-component-popup fixed z-[1000] flex min-w-[8rem] flex-col gap-1 overflow-hidden rounded-md border border-gray-300 bg-white px-2 py-1 shadow-lg dark:border-border dark:bg-background"
            style={{
              left: adjustedContextMenuPosition?.x ?? contextMenuPosition.x,
              top: adjustedContextMenuPosition?.y ?? contextMenuPosition.y,
              visibility: adjustedContextMenuPosition ? "visible" : "hidden",
            }}
            onMouseDown={event => event.stopPropagation()}
            onContextMenu={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {activeContextMenuActions.map(action => {
              const Icon = action.icon;

              return (
                <button
                  key={action.id}
                  type="button"
                  className="flex cursor-pointer select-none items-center rounded px-2 py-1.5 text-sm text-gray-800 outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100 dark:text-foreground dark:hover:bg-muted dark:focus:bg-muted"
                  onClick={() => {
                    action.onSelect();
                    closeContextMenu();
                  }}
                >
                  <span className="mr-2 flex h-4 w-4 items-center text-gray-500 dark:text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 whitespace-nowrap text-left">{action.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      data-smart-table-tour-staleness={isVisuallyStale ? "true" : undefined}
      className={`relative h-full w-full ${isVisuallyStale ? "opacity-75" : ""}`}
      title={isVisuallyStale ? STALE_TOOLTIP : undefined}
      onMouseEnter={scheduleActionAffordances}
      onMouseLeave={hideActionAffordances}
      onFocusCapture={showActionAffordances}
      onBlurCapture={handleActionAffordanceBlur}
      onContextMenu={handleContextMenu}
    >
      {uploadInput}
      {innerEl}
      {shouldRenderMetricsOverlay ? (
        <PromptRequestMetricsChip metrics={executionMetricsOverlay} />
      ) : null}
      {shouldRenderActions ? (
        <CellActions
          onOpen={resolvedOnOpen}
          onCopy={onCopyCell}
          onImport={onImportAnchor}
          importLabel={importAnchorLabel}
          onRun={onRunCell}
          onStop={onStopCell}
          onUpload={
            shouldShowUploadButton
              ? () => {
                  uploadInputRef.current?.click();
                }
              : undefined
          }
          openRequestUrl={openRequestUrl}
          openRequestLabel={openRequestLabel}
          isVisible={shouldShowActions}
          className="right-1"
        />
      ) : null}
      {contextMenu}
    </div>
  );
}

export default memo(CellPreview);
